import { type PowerSyncDrizzleDB, user } from "@comandero/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`: a
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). `user` is NOT outside this window —
 * and remains not outside even though `ThemeProvider` no longer seeds local row
 * for real accounts (`uid !== VISITOR_ID` guard, 2026-08-13): why
 * complete protection remains despite fix is documented ONCE,
 * in `../powersync/verified-write.ts`. Mutations **can
 * throw**: caller must communicate it (already done with `try/catch` in
 * all `apps/web` call sites).
 *
 * Without `scope`: unlike `order`/`register_session`, `user` has no
 * org variants — `infra/sync_streams.yaml` syncs own row with
 * `WHERE id = auth.user_id()` (single global row per user,
 * never an org copy), and the stream bringing rows of OTHER
 * members (`org_members`) only projects `id, name, email, image` — never the
 * preference columns written by these mutations. No scenario of
 * "same id, another organization" to protect here.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme) and not through REST: this way
 * the new photo is visible immediately and survives a cold reload without
 * a connection. The BINARY does go through REST (`POST /api/avatar`) because it does not fit in the
 * sync queue; what travels here is only the key returned by that upload.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no syncable row of its own and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as there is for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
